import { useEffect } from 'react'
import { useRouter } from 'next/router'
import Image from 'next/image'
import Trans from 'next-translate/Trans'
import { useSurveyTranslations } from '@creativegs/pipe-toolkit/utils/translation'

import { oSeriesPages } from 'types/oSeriesPages'
import useCreateOrder from 'utils/hooks/useCreateOrder'
import { PIPELINE_ID } from 'types/order'

import Head from './common/Head'
import LandingButton from './common/landing/LandingButton'
import LandingFooter from './common/landing/LandingFooter'
import Bubbles, { Bubble } from './common/landing/Bubbles'
import LandingHeader from './common/landing/LandingHeader'
import { isNewFooterAllowed } from 'utils/localeHelper'
import HelpdeskLandingFooter from './common/HelpdeskLandingFooter'

const Landing: React.FC = () => {
  const router = useRouter()
  const { t } = useSurveyTranslations('0-landing')
  const { isLoading } = useCreateOrder()

  useEffect(() => {
    document.body.classList.add('bg-green-800')

    return () => document.body.classList.remove('bg-green-800')
  }, [])

  const onSubmit = async (e) => {
    e.preventDefault()
    const pipeLineID = PIPELINE_ID[router.locale][router.query.pipe]
    let pipeSeries = router.query.pipe

    if (!pipeLineID) {
      pipeSeries = 'o'
    }

    router.push(`/${router.locale}/${pipeSeries}/${oSeriesPages.startTest}`)
  }

  const renderTitle = () => {
    const {
      locale,
      query: { pipe },
    } = router

    if (locale === 'en-IE' && pipe === 'o-1295') {
      return (
        <Trans
          i18nKey="o_series/survey/app:app.0-landing.title_2"
          components={[<span key="Go" className="text-green-200" />]}
        />
      )
    }

    return (
      <Trans
        i18nKey="o_series/survey/app:app.0-landing.title"
        components={[<span key="Go" className="text-green-200" />]}
      />
    )
  }

  return (
    <>
      <Head />
      <div className="overflow-x-hidden">
        <Bubbles bubblePosition={Bubble.top} />
        <div className="px-7 container max-w-screen-md mx-auto pb-24">
          <div className="min-h-screen">
            <LandingHeader />
            <main className="flex flex-col items-center">
              <h1 className="text-white text-3xl mb-8 text-center z-10 font-bold">
                {renderTitle()}
              </h1>
              <img
                className="animate-high-bounce mb-4"
                width="20"
                height="20"
                src="/assets/o_series/landing/chevron.svg"
                alt="Bouncing arrow pointing down on the button"
              />
              <form
                onSubmit={onSubmit}
                className="flex justify-center space-x-10 mb-8"
              >
                <LandingButton
                  name="continue"
                  loading={isLoading}
                  disabled={isLoading}
                  textAlign="center"
                  t={t}
                />
              </form>
              <div className="relative w-full max-w-lg mx-auto md:-mt-12">
                <Bubbles bubblePosition={Bubble.middle} />
              </div>
              <div className="relative w-96 h-96 self-end -mr-32 md:-mr-4 lg:-mr-48">
                <Image
                  layout="fill"
                  objectFit="cover"
                  src="/assets/o_series/landing/avocado.png"
                  alt="Avocado cut in half"
                />
              </div>
            </main>
            {isNewFooterAllowed(router.locale) ? (
              <HelpdeskLandingFooter />
            ) : (
              <LandingFooter />
            )}
          </div>
        </div>
        <Bubbles bubblePosition={Bubble.bottom} />
      </div>
    </>
  )
}

export default Landing
